import { useCallback, useState } from "react";

export interface UseCopyToClipboardResult {
  copied: boolean;
  copy: (text: string) => void;
}

const COPIED_RESET_DELAY_MS = 2000;

/**
 * Copies text to the clipboard, flashing `copied` true for a couple of
 * seconds afterward. Prefers `navigator.clipboard.writeText`, falling back to
 * a hidden-textarea `document.execCommand("copy")` when it's unavailable or
 * throws — the Clipboard API requires a secure context, which local dev
 * (served over plain `http://`) is not.
 */
export const useCopyToClipboard = (): UseCopyToClipboardResult => {
  const [copied, setCopied] = useState(false);

  const flash = useCallback(() => {
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_RESET_DELAY_MS);
  }, []);

  const fallbackCopy = useCallback(
    (text: string) => {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;pointer-events:none";
      // Astryx Dialog uses the native <dialog> element with showModal(), which
      // places it in the browser's top layer. The browser treats everything in
      // document.body behind the top layer as inert — ta.select() fails and
      // execCommand("copy") has nothing to act on. Appending inside the open
      // <dialog> keeps the element in the same top-layer context.
      const container = document.querySelector("dialog[open]") ?? document.body;
      container.appendChild(ta);
      ta.focus();
      ta.select();
      try {
        document.execCommand("copy");
        flash();
      } finally {
        container.removeChild(ta);
      }
    },
    [flash],
  );

  const copy = useCallback(
    (text: string) => {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(flash).catch(() => fallbackCopy(text));
      } else {
        fallbackCopy(text);
      }
    },
    [flash, fallbackCopy],
  );

  return { copied, copy };
};
